import {
  Component,
  h,
  State,
  Prop,
  Event,
  EventEmitter,
  ComponentInterface,
} from "@stencil/core";
import {
  ThemeService,
  TranslateService,
  FormHelper,
  AppConfigService as Config,
  AppConfigService,
} from "../../../services";
import {
  ICustomField,
  ISelectOption,
  IThemeOptions,
  RegistrationTypeEnum,
} from "../../../models";
import { ISCheduleContactBase } from "../../../models/schedule/ISchedule";
import { Subscription } from "rxjs";
import { NotificationService } from "../../../services/notification.service";
import { ErrorCode } from "@auvious/integrations";

interface IState {
  contact?: IContact;
  errors?: {
    name?: ValidityState;
    email?: ValidityState;
    phone?: ValidityState;
  };
  touched?: {
    name?: boolean;
    email?: boolean;
    phone?: boolean;
  };
  isCustomFieldsValid?: boolean;
  isInverted?: boolean;
}

const DEFAULT_STATE: IState = {
  errors: {},
  touched: {},
  contact: {},
  isCustomFieldsValid: true,
  isInverted: false,
};

interface IContact {
  name?: string;
  phone?: string;
  phoneCountryCode?: string;
  email?: string;
}

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/; // valid E.164
// const PHONE_REGEX = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;

@Component({
  tag: "app-schedule-contact",
  styleUrl: "app-schedule-contact.scss",
  shadow: true,
})
export class AppScheduleContact implements ComponentInterface {
  @Prop({ mutable: true }) contact: ISCheduleContactBase = {};

  @State() state = { ...DEFAULT_STATE };
  @State() themeOptions: IThemeOptions;

  @Event() contactChanged: EventEmitter<ISCheduleContactBase>;

  private form: HTMLFormElement;
  private phoneRegex = new RegExp(PHONE_REGEX);
  private countryDialCodeSelection: ISelectOption;
  private configSubscription: Subscription;
  private customFields: ICustomField[] = [];
  private isCustomRegistration: boolean = false;

  constructor() {
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
    FormHelper.registerValidationFn(this.validateForm.bind(this));
  }

  componentWillLoad(): void | Promise<void> {
    ThemeService.optionsAvailable().subscribe((options) => {
      if (options.direction === "rtl") {
        this.setState({ isInverted: true });
      }
    });

    this.configSubscription = AppConfigService.optionsReady.subscribe(
      (config) => {
        this.customFields = config.customFields;
        this.isCustomRegistration =
          config.registrationType === RegistrationTypeEnum.custom;
      }
    );

    if (this.isCustomRegistration) {
      if (this.customFieldsValid()) {
        this.setupCustomFields();
      } else {
        this.state = { ...this.state, isCustomFieldsValid: false };
      }
    }

    this.setState({
      contact: {
        name: this.getContactName() || Config.customerDisplayName,
        phone: this.contact?.contact?.primaryPhone,
        phoneCountryCode: this.contact?.contact?.primaryPhoneCountryCode,
        email: this.contact?.contact?.primaryEmail || Config.customerEmail,
      },
    });
  }

  setupCustomFields() {
    // set default values
    AppConfigService.customFields.forEach((field) => {
      this.contact = {
        ...this.contact,
        metadata: {
          ...this.contact.metadata,
          [field.name]:
            field.type === "select"
              ? this.contact.metadata?.[field.name] ?? field.options?.[0]?.value
              : this.contact.metadata?.[field.name],
        },
      };
      this.setState({
        touched: { ...this.state.touched, [field.name]: false },
      });
    });
  }

  getContactName(): string {
    const name = [];
    this.contact?.name?.firstName &&
      name.push(this.contact?.name?.firstName.trim());
    if (!!this.contact.metadata && !("lastName" in this.contact.metadata)) {
      this.contact?.name?.lastName &&
        name.push(this.contact?.name?.lastName.trim());
    }

    return name.join(" ").trim();

    /**
      [
        this.sessionContact.firstName?.trim(),
        this.sessionContact.lastName?.trim(),
      ]
        .flatMap((f) => (f ? [f] : [])) // remove undefined & null
        .join(" "),
     */
  }

  disconnectedCallback(): void {
    this.configSubscription.unsubscribe();
    this.customFields = [];

    if (!this.form) {
      return;
    }
    // @ts-ignore
    for (let item of this.form.elements) {
      item.value = null;
    }
  }

  private setState(state: IState) {
    this.state = { ...this.state, ...state };
  }

  handleChange(event) {
    this.setState({
      contact: {
        ...this.state.contact,
        [event.target.name]: event.target.value,
      },
      errors: {
        ...this.state.errors,
        [event.target.name]: event.target.checkValidity()
          ? null
          : event.target.validity,
      },
    });
    this.notifyChange();
  }

  handleMetaChange(event) {
    this.setState({
      errors: {
        ...this.state.errors,
        [event.target.name]: event.target.checkValidity()
          ? null
          : event.target.validity,
      },
    });
    this.contact = {
      ...this.contact,
      metadata: {
        ...this.contact.metadata,
        [event.target.name]: event.target.value,
      },
    };
    if (event.target.name === "email" || event.target.type === "email") {
      this.setState({
        contact: {
          ...this.state.contact,
          email: event.target.value,
        },
      });
    }
    this.notifyChange();
  }

  handleCountryDialCodeChange(e: CustomEvent) {
    this.countryDialCodeSelection = e.detail;
    if (this.state.contact?.phone) {
      this.setState({
        contact: {
          ...this.state.contact,
          phoneCountryCode: this.countryDialCodeSelection?.value,
          // phone: this.state.contact.phone.replace(
          //   this.countryDialCodeSelection?.value,
          //   ""
          // ),
        },
      });
    }
    this.notifyChange();
  }

  registerTouchChange(element: HTMLInputElement) {
    element?.addEventListener("click", (_) => {
      this.markAsTouched(element.name);
    });
    element?.addEventListener("touchend", (_) => {
      this.markAsTouched(element.name);
    });
    element?.addEventListener("blur", (_) => {
      this.isValid();
    });
  }

  markAsTouched(name) {
    this.setState({
      touched: {
        ...this.state.touched,
        [name]: true,
      },
    });
  }

  private notifyChange() {
    if (this.isValid()) {
      this.contactChanged.emit({
        contact: {
          primaryEmail: this.state.contact.email,
          primaryPhone: this.state.contact.phone,
          primaryPhoneCountryCode:
            this.state.contact.phoneCountryCode ??
            this.countryDialCodeSelection.value,
        },
        name: {
          firstName: this.state.contact.name,
          lastName: this.contact.metadata?.lastName,
        },
        metadata: this.contact.metadata,
      });
    }
  }

  validateForm(): boolean {
    const touched = {
      name: true,
      phone: true,
      email:
        this.isAppointment && AppConfigService.scheduleCustomerEmailRequired,
    };
    if (this.isCustomRegistration) {
      this.customFields.forEach((field) => {
        touched[field.name] = true;
      });
    }
    this.setState({ touched });
    return this.isValid();
  }

  private customFieldsValid(): boolean {
    let error = false;

    const propError = (message, errorCode) => {
      NotificationService.error(message, { ttl: -1, errorCode });
      error = true;
    };

    if (AppConfigService.customFields.length === 0) {
      propError(
        '"registration-type" is set to "custom" but custom fields are missing.',
        ErrorCode.REGISTRATION_MISSING_CUSTOM_FIELDS
      );
    } else {
      let valid = true;
      AppConfigService.customFields.forEach((field) => {
        valid =
          valid &&
          !!field.name &&
          field.type &&
          ((field.type === "select" && field.options?.length > 0) ||
            field.type !== "select");
      });
      if (!valid) {
        propError(
          'invalid custom fields. Please make sure "name" and "type" are set and "select" has one or more options.',
          ErrorCode.REGISTRATION_INVALID_CUSTOM_FIELDS
        );
      }
    }
    return !error;
  }

  private isValid(): boolean {
    let valid = true;

    // @ts-ignore
    for (let item of this.form.elements) {
      this.state = {
        ...this.state,
        errors: {
          ...this.state.errors,
          [item.name]: this.state.touched[item.name]
            ? item.checkValidity()
              ? null
              : item.validity
            : null,
        },
      };
      valid = this.state.touched[item.name]
        ? valid && item.checkValidity()
        : true;
    }
    return valid;
  }

  private renderError(field: ICustomField, warnTextStyle) {
    return (
      <div style={warnTextStyle} class="error-text">
        {(this.state.errors[field.name] as ValidityState).valueMissing && (
          <div>{TranslateService.instant("Required")}</div>
        )}
        {(this.state.errors[field.name] as ValidityState).typeMismatch && (
          <div>{TranslateService.instant("Not an email")}</div>
        )}
        {(this.state.errors[field.name] as ValidityState).badInput && (
          <div>{TranslateService.instant("Not an number")}</div>
        )}
        {(this.state.errors[field.name] as ValidityState).patternMismatch && (
          <div>
            {field.patternErrorMessage || TranslateService.instant("Invalid")}
          </div>
        )}
      </div>
    );
  }

  private renderText(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        <input
          ref={(el) => this.registerTouchChange(el)}
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          type="text"
          required={field.required}
          onInput={this.handleMetaChange.bind(this)}
          placeholder={field.label}
          value={this.contact.metadata?.[field.name]}
          pattern={field.pattern}
        />
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderEmail(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        <input
          ref={(el) => this.registerTouchChange(el)}
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          type="email"
          required={field.required}
          onInput={this.handleMetaChange.bind(this)}
          placeholder={field.label}
          value={this.contact.metadata?.[field.name]}
          pattern={field.pattern}
        />
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderNumber(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        <input
          ref={(el) => this.registerTouchChange(el)}
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          type="number"
          required={field.required}
          onInput={this.handleMetaChange.bind(this)}
          placeholder={field.label}
          value={this.contact.metadata?.[field.name]}
          pattern={field.pattern}
        />
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderSelect(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-select": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        {!!field.label && <label>{field.label}</label>}
        <select
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          required={field.required}
          onChange={this.handleMetaChange.bind(this)}
        >
          {field.options?.map((option, index) => {
            return (
              <option
                value={option.value || ""}
                selected={
                  (this.contact.metadata &&
                    this.contact.metadata[field.name] === option.value) ??
                  index === 0
                }
                disabled={!option.value}
              >
                {option.label}
              </option>
            );
          })}
        </select>
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderNotSupported(field: ICustomField) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-not-supported": true,
        }}
      >
        <av-translate>Unsupported field type:</av-translate> {field.type}
      </div>
    );
  }

  private get isAppointment() {
    return ["genesys-appointment", "talkdesk-appointment"].includes(
      Config.scheduleMode
    );
  }

  render() {
    const nameText = TranslateService.instant("Name");
    const phoneText = TranslateService.instant("Mobile Phone number");
    const warnInputStyle = { borderBottomColor: this.themeOptions.colorWarn };
    const warnTextStyle = { color: this.themeOptions.colorWarn };

    return (
      <form ref={(el) => (this.form = el)}>
        <div
          class={{
            "form-field": true,
            "form-field-error": !!this.state.errors.name,
            "form-field-inverted": this.state.isInverted,
          }}
        >
          <input
            ref={(el) => this.registerTouchChange(el)}
            style={!!this.state.errors.name ? warnInputStyle : null}
            name="name"
            type="text"
            required
            onInput={this.handleChange.bind(this)}
            placeholder={nameText}
            value={this.state.contact.name}
          />
          {!!this.state.errors.name && this.state.errors.name.valueMissing && (
            <span style={warnTextStyle} class="error-text">
              <av-translate>Required</av-translate>
            </span>
          )}
        </div>

        {this.isAppointment &&
          AppConfigService.scheduleCustomerEmailRequired && (
            <div
              class={{
                "form-field": true,
                "form-field-error": !!this.state.errors.email,
                "form-field-inverted": this.state.isInverted,
              }}
            >
              <input
                ref={(el) => this.registerTouchChange(el)}
                style={!!this.state.errors.email ? warnInputStyle : null}
                name="email"
                type="email"
                required
                onInput={this.handleChange.bind(this)}
                placeholder="email"
                value={this.state.contact.email}
              />
              {!!this.state.errors.email &&
                this.state.errors.email.valueMissing && (
                  <span style={warnTextStyle} class="error-text">
                    <av-translate>Required</av-translate>
                  </span>
                )}
              {!!this.state.errors.email &&
                this.state.errors.email.typeMismatch && (
                  <span style={warnTextStyle} class="error-text">
                    <av-translate>Invalid email</av-translate>
                  </span>
                )}
            </div>
          )}

        {this.isCustomRegistration && this.state.isCustomFieldsValid && (
          <span>
            {this.customFields.map((field) => {
              switch (field.type) {
                case "text":
                  return this.renderText(field, warnTextStyle, warnInputStyle);
                case "select":
                  return this.renderSelect(
                    field,
                    warnTextStyle,
                    warnInputStyle
                  );
                case "email":
                  return this.renderEmail(field, warnTextStyle, warnInputStyle);
                case "number":
                  return this.renderNumber(
                    field,
                    warnTextStyle,
                    warnInputStyle
                  );
                default:
                  return this.renderNotSupported(field);
              }
            })}
          </span>
        )}

        {this.isCustomRegistration && !this.state.isCustomFieldsValid && (
          <p class="text-muted">Invalid fields</p>
        )}

        <div
          role="group"
          aria-label={TranslateService.instant("Your mobile phone number")}
          class={{
            "form-field": true,
            "form-field-error": !!this.state.errors.phone,
            "form-field-inverted": this.state.isInverted,
          }}
        >
          <div class="form-field-phone">
            <div>
              <av-select-country-dial-code
                countryCode={this.state.contact?.phoneCountryCode}
                onCodeChange={this.handleCountryDialCodeChange.bind(this)}
              ></av-select-country-dial-code>
            </div>
            <input
              ref={(el) => this.registerTouchChange(el)}
              style={!!this.state.errors.phone ? warnInputStyle : null}
              name="phone"
              type="text"
              pattern={this.phoneRegex.source}
              required
              onInput={this.handleChange.bind(this)}
              placeholder={phoneText}
              value={this.state.contact.phone}
            />
          </div>
          <small>
            <av-translate>You will receive a one-time-password</av-translate>
          </small>
          {!!this.state.errors.phone &&
            this.state.errors.phone.valueMissing && (
              <span style={warnTextStyle} class="error-text error-text-phone">
                <av-translate>Required</av-translate>
              </span>
            )}
          {!!this.state.errors.phone &&
            this.state.errors.phone.patternMismatch && (
              <span style={warnTextStyle} class="error-text error-text-phone">
                <av-translate>Invalid phone number</av-translate>
              </span>
            )}
        </div>
      </form>
    );
  }
}
